import React, { useState, useEffect } from 'react';
import { sapApi, finalyzerApi } from '../services/api';
import TBDataViewer from './TBDataViewer';
import '../styles/Panel.css';

function StatusPanel({ entity, refreshTrigger, dataPoint = 'tb' }) {
  const [tbStatus, setTbStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showViewer, setShowViewer] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null); // Track which version is being viewed
  const [pushingVersion, setPushingVersion] = useState(null); // Track which version is being pushed
  const [pushResult, setPushResult] = useState(null);
  const [pushError, setPushError] = useState(null);

  useEffect(() => {
    loadStatus();
  }, [entity, refreshTrigger]);

  const loadStatus = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await sapApi.getTBStatus(entity.name);
      setTbStatus(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePushToFinalyzer = async (version) => {
    setPushingVersion(version.timestamp); // Track which version is being pushed
    setPushError(null);
    setPushResult(null);

    try {
      // Use start_date and end_date from version
      const startDate = version.start_date;
      const endDate = version.end_date;

      if (!endDate) {
        throw new Error('End date information not available');
      }

      const result = await finalyzerApi.pushTrialBalance(entity.name, endDate, startDate);
      setPushResult(result);

      // Auto-clear success message after 5 seconds
      setTimeout(() => setPushResult(null), 5000);
    } catch (err) {
      setPushError(err.message);
    } finally {
      setPushingVersion(null); // Clear the pushing state
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // Format date as DD MMM YYYY (e.g., 01 Apr 2025)
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Return as-is if invalid

    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('en-GB', options);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    // Format: YYYYMMDD_HHMMSS -> DD MMM YYYY, HH:MM:SS
    const year = timestamp.substring(0, 4);
    const month = timestamp.substring(4, 6);
    const day = timestamp.substring(6, 8);
    const hour = timestamp.substring(9, 11);
    const minute = timestamp.substring(11, 13);
    const second = timestamp.substring(13, 15);

    const date = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
    const dateStr = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    return `${dateStr}, ${hour}:${minute}:${second}`;
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    // Format ISO date as DD MMM YYYY, HH:MM:SS
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return 'N/A';

    const dateStr = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const timeStr = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    return `${dateStr}, ${timeStr}`;
  };

  if (loading) {
    return (
      <div className="panel status-panel">
        <h2>TB Status & History</h2>
        <div className="loading">Loading status...</div>
      </div>
    );
  }

  return (
    <div className="panel status-panel">
      <h2>TB Status & History</h2>

      {error && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {pushResult && (
        <div className="alert alert-success">
          <strong>Success!</strong> {pushResult.message}
          {pushResult.finalyzer_id && <p>FinAlyzer ID: {pushResult.finalyzer_id}</p>}
        </div>
      )}

      {pushError && (
        <div className="alert alert-error">
          <strong>Push Failed:</strong> {pushError}
        </div>
      )}

      {tbStatus && (
        <>
          <div className="status-info">
            <p><strong>Entity:</strong> {tbStatus.entity_name}</p>
            <p><strong>Environment:</strong> {tbStatus.environment}</p>
            <p><strong>Total Versions:</strong> {tbStatus.total_versions}</p>
          </div>

          {tbStatus.versions && tbStatus.versions.length > 0 ? (
            <div className="versions-list">
              <h3>Available TB Versions</h3>
              <table className="versions-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Timestamp</th>
                    <th>Records</th>
                    <th>Upload Time</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tbStatus.versions.map((version, index) => (
                    <tr key={version.timestamp} className={index === 0 ? 'latest' : ''}>
                      <td>{index + 1}</td>
                      <td>{formatDate(version.start_date)}</td>
                      <td>{formatDate(version.end_date)}</td>
                      <td>{formatTimestamp(version.timestamp)}</td>
                      <td>{version.record_count || 'N/A'}</td>
                      <td>{formatDateTime(version.upload_time)}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn btn-view"
                            onClick={() => {
                              setSelectedVersion(version);
                              setShowViewer(true);
                            }}
                          >
                            View
                          </button>
                          <button
                            className="btn btn-push"
                            onClick={() => handlePushToFinalyzer(version)}
                            disabled={pushingVersion === version.timestamp}
                          >
                            {pushingVersion === version.timestamp ? 'Pushing...' : 'Push to FinAlyzer'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="help-text">Latest version is highlighted and will be used for pushing to FinAlyzer</p>
            </div>
          ) : (
            <div className="alert alert-info">
              <p>No TB versions found. Pull Trial Balance from SAP to create the first version.</p>
            </div>
          )}
        </>
      )}

      <button className="btn btn-secondary" onClick={loadStatus}>
        Refresh Status
      </button>

      {showViewer && selectedVersion && (
        <TBDataViewer
          entity={entity}
          dataPoint={dataPoint}
          startDate={selectedVersion.start_date}
          endDate={selectedVersion.end_date}
          onClose={() => {
            setShowViewer(false);
            setSelectedVersion(null);
          }}
        />
      )}
    </div>
  );
}

export default StatusPanel;
